import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { fetchBinders, createBinder, updateBinder, deleteBinder } from '../../api/binders';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';

const MyBindersPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const [binders, setBinders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTitle, setNewTitle] = useState('');
    const [editingID, setEditingID] = useState(null);
    const [editTitle, setEditTitle] = useState('');

    const loadBinders = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const data = await fetchBinders(currentUser.username);
            setBinders(data);
        } catch {
            toast.error('Failed to load binders');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => { loadBinders(); }, [loadBinders]);

    const handleCreate = async e => {
        e.preventDefault();
        if (!newTitle.trim()) return;
        try {
            await createBinder(newTitle.trim());
            setNewTitle('');
            await loadBinders();
            toast.success('Binder created');
        } catch {
            toast.error('Failed to create binder');
        }
    };

    const handleRename = async binderID => {
        if (!editTitle.trim()) return;
        try {
            await updateBinder(binderID, editTitle.trim());
            setEditingID(null);
            await loadBinders();
            toast.success('Binder renamed');
        } catch {
            toast.error('Failed to rename binder');
        }
    };

    const handleDelete = async binderID => {
        if (!window.confirm('Delete this binder?')) return;
        try {
            await deleteBinder(binderID);
            await loadBinders();
            toast.success('Binder deleted');
        } catch {
            toast.error('Failed to delete binder');
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="my-binders-page">
            <h2>My Binders</h2>

            <form className="binder-create-form" onSubmit={handleCreate}>
                <input
                    type="text"
                    placeholder="New binder name..."
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    maxLength={128}
                />
                <button type="submit">Create Binder</button>
            </form>

            {binders.length === 0 ? (
                <p>No binders yet. Create one above!</p>
            ) : (
                <ul className="binder-list">
                    {binders.map(binder => (
                        <li key={binder.binderID} className="binder-list-item">
                            {editingID === binder.binderID ? (
                                <span className="binder-rename">
                                    <input
                                        value={editTitle}
                                        onChange={e => setEditTitle(e.target.value)}
                                        maxLength={128}
                                        autoFocus
                                    />
                                    <button onClick={() => handleRename(binder.binderID)}>Save</button>
                                    <button onClick={() => setEditingID(null)}>Cancel</button>
                                </span>
                            ) : (
                                <>
                                    <span className="binder-info">
                                        <strong>{binder.title}</strong>
                                        <span className="binder-card-count">{binder.cardCount} cards</span>
                                    </span>
                                    <span className="binder-actions">
                                        <Link to={`/${currentUser.username}/binder/${binder.binderNumber}`} className="button">
                                            View / Edit
                                        </Link>
                                        <button
                                            onClick={() => {
                                                setEditingID(binder.binderID);
                                                setEditTitle(binder.title);
                                            }}
                                        >
                                            Rename
                                        </button>
                                        <button
                                            className="btn-danger"
                                            onClick={() => handleDelete(binder.binderID)}
                                        >
                                            Delete
                                        </button>
                                    </span>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default MyBindersPage;
